import { computed, ref, watch } from 'vue';
import type {
  PageNumber,
  PaginationQueryAction,
  PaginationQueryState,
  PaginationTableColumn,
  PaginationTableViewAction,
  PaginationTableViewProps,
  SortDirection,
} from '../types';

export type { PaginationQueryAction, PaginationQueryState };

type LogicProps<TRow extends Record<string, unknown>> = {
  columns: PaginationTableColumn<TRow>[];
  rows: TRow[];
  rowClass?: (row: TRow) => string | Record<string, boolean> | undefined;
  totalItems: number;
  initialPage?: number;
  initialPageSize?: number;
  pageSizeOptions?: number[];
  /** Must be an odd number >= 5. Defaults to 7. */
  maxPageButtons?: number;
  /** When provided, the table syncs its sort/page state to this value reactively. */
  query?: PaginationQueryState;
  isLoading?: boolean;
};

const DEFAULT_PAGE_SIZE = 50;

export function usePaginationTableLogic<TRow extends Record<string, unknown>>(
  props: LogicProps<TRow>,
  onQueryChange: (action: PaginationQueryAction) => void,
) {
  const currentPage = ref(props.query?.page || props.initialPage || 1);
  const pageSize = ref(
    props.query?.pageSize || props.initialPageSize || DEFAULT_PAGE_SIZE,
  );
  const sortColumnKey = ref<string | null>(props.query?.sortColumnKey ?? null);
  const sortDirection = ref<SortDirection>(
    props.query?.sortDirection ?? undefined,
  );

  watch(
    () => props.query,
    (q) => {
      if (!q) return;
      currentPage.value = q.page;
      pageSize.value = q.pageSize || DEFAULT_PAGE_SIZE;
      sortColumnKey.value = q.sortColumnKey;
      sortDirection.value = q.sortDirection;
    },
    { deep: true },
  );

  const totalPages = computed(() =>
    Math.max(1, Math.ceil(props.totalItems / pageSize.value)),
  );
  const rangeStart = computed(() =>
    props.totalItems === 0 ? 0 : (currentPage.value - 1) * pageSize.value + 1,
  );
  const rangeEnd = computed(() =>
    Math.min(currentPage.value * pageSize.value, props.totalItems),
  );
  const columnSortDirections = computed(() =>
    Object.fromEntries(
      props.columns.map((col) => [
        col.key,
        sortColumnKey.value === col.key ? sortDirection.value : undefined,
      ]),
    ),
  );

  const pageNumbers = computed<PageNumber[]>(() => {
    const total = totalPages.value;
    const current = currentPage.value;
    const max = props.maxPageButtons ?? 7;

    if (total <= max) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    // Slots when only one side has ellipsis: first/last + dots fills 2 slots
    const edgeCount = max - 2;
    // Slots in the middle when both sides have ellipsis
    const middleCount = max - 4;
    const middleSide = Math.floor(middleCount / 2);

    const showLeftDots = current > edgeCount - 1;
    const showRightDots = current < total - edgeCount + 2;

    if (!showLeftDots) {
      return [
        ...(Array.from({ length: edgeCount }, (_, i) => i + 1) as number[]),
        '...',
        total,
      ];
    }

    if (!showRightDots) {
      return [
        1,
        '...',
        ...(Array.from(
          { length: edgeCount },
          (_, i) => total - edgeCount + 1 + i,
        ) as number[]),
      ];
    }

    return [
      1,
      '...',
      ...(Array.from(
        { length: middleCount },
        (_, i) => current - middleSide + i,
      ) as number[]),
      '...',
      total,
    ];
  });

  const viewProps = computed<PaginationTableViewProps<TRow>>(() => ({
    columns: props.columns,
    rows: props.rows,
    rowClass: props.rowClass,
    currentPage: currentPage.value,
    pageSize: pageSize.value,
    pageSizeOptions: props.pageSizeOptions ?? [10, 25, 50, 100],
    totalItems: props.totalItems,
    totalPages: totalPages.value,
    rangeStart: rangeStart.value,
    rangeEnd: rangeEnd.value,
    columnSortDirections: columnSortDirections.value,
    pageNumbers: pageNumbers.value,
    isLoading: props.isLoading,
  }));

  function queryState(): PaginationQueryState {
    return {
      page: currentPage.value,
      pageSize: pageSize.value,
      sortColumnKey: sortColumnKey.value,
      sortDirection: sortDirection.value,
    };
  }

  function onAction(action: PaginationTableViewAction) {
    switch (action.type) {
      case 'column-sort': {
        const { columnKey } = action.payload;
        if (sortColumnKey.value === columnKey) {
          sortDirection.value =
            sortDirection.value === 'asc'
              ? 'desc'
              : sortDirection.value === 'desc'
                ? undefined
                : 'asc';
          if (sortDirection.value === undefined) {
            sortColumnKey.value = null;
          }
        } else {
          sortColumnKey.value = columnKey;
          sortDirection.value = 'asc';
        }
        currentPage.value = 1;
        onQueryChange({ type: 'column-sort', payload: queryState() });
        break;
      }
      case 'page-change': {
        currentPage.value = action.payload.page;
        onQueryChange({ type: 'page-change', payload: queryState() });
        break;
      }
      case 'page-size-change': {
        pageSize.value = action.payload.pageSize || DEFAULT_PAGE_SIZE;
        currentPage.value = 1;
        onQueryChange({ type: 'page-size-change', payload: queryState() });
        break;
      }
    }
  }

  return {
    viewProps,
    onAction,
  };
}
